// A delivery "slot" is a day+time the zip supports, priced per combination in
// Airtable (see backend /api/delivery-availability/:zip). An order pays the sum
// of every UNIQUE slot its meals use — four meals on Sunday evening cost one
// Sunday-evening fee, not four. The endpoint resolves each slot's `deliveryDate`
// and only returns still-orderable ones, so nothing here re-derives a date.
import { DAY_ORDER, formatConcreteDeliveryDate } from './deliveryDates';

const TIME_ORDER = ['morning', 'midday', 'evening'];

export const titleCase = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');

/** The single value a cart line stores for its delivery. Lowercased so a line
 *  saved as "Sunday" still matches a slot list in another casing. */
export function slotKey(day, time) {
  return `${String(day || '').trim().toLowerCase()}|${String(time || '').trim().toLowerCase()}`;
}

/** Halves, for the fee lookup and the `Delivery Time` column. */
export function parseSlotKey(key) {
  const [day = '', time = ''] = String(key || '').split('|');
  return { day, time };
}

/** Day-then-time order. The backend capitalizes day/time ("Sunday", "Evening"),
 *  so compare lowercased rather than assuming casing matches. */
export function sortSlots(slots = []) {
  return [...slots].sort((a, b) => {
    const dayDiff = DAY_ORDER.indexOf(a.day.toLowerCase()) - DAY_ORDER.indexOf(b.day.toLowerCase());
    if (dayDiff !== 0) return dayDiff;
    return TIME_ORDER.indexOf(a.time.toLowerCase()) - TIME_ORDER.indexOf(b.time.toLowerCase());
  });
}

/** Shows the actual date, not the bare weekday. Falls back to the weekday
 *  name if a slot has no resolved date yet. */
export function slotLabel(slot) {
  if (!slot) return '';
  const datePart = slot.deliveryDate ? formatConcreteDeliveryDate(slot.deliveryDate) : titleCase(slot.day);
  return `${datePart} · ${titleCase(slot.time)}`;
}

/** The slot new meals default to: Sunday evening where the zip supports it,
 *  otherwise the earliest available. */
export function preferredSlot(sortedSlots = []) {
  if (!sortedSlots.length) return null;
  return (
    sortedSlots.find((s) => s.day.toLowerCase() === 'sunday' && s.time.toLowerCase() === 'evening') ||
    sortedSlots[0]
  );
}

/** Key-only convenience for a `<select>`'s `value` — see preferredSlot. */
export function preferredSlotKey(sortedSlots = []) {
  const preferred = preferredSlot(sortedSlots);
  return preferred ? slotKey(preferred.day, preferred.time) : '';
}

/** Falls back to the order-level default for a line that never got a slot (an
 *  older cart restored from localStorage). */
export function resolveLineSlotKey(line, delivery) {
  return line?.deliverySlot || slotKey(delivery?.deliveryDay, delivery?.deliveryTime);
}

/**
 * The cart split into one entry per slot in use, ordered by day/time. `fee` is
 * null when the slot isn't in `availableSlots` (no zip yet, or one this zip
 * doesn't serve). `lines` are the raw cart lines on that slot — group them by
 * customization signature (see utils/cartSignature.js) for display.
 * @returns {Array<{key, day, time, fee, deliveryDate, label, lines}>}
 */
export function groupCartByDelivery(cart = [], availableSlots = [], delivery = null) {
  const slotByKey = new Map(availableSlots.map((s) => [slotKey(s.day, s.time), s]));
  const groups = new Map();

  cart.forEach((line) => {
    const key = resolveLineSlotKey(line, delivery);
    if (!groups.has(key)) {
      const slot = slotByKey.get(key);
      const { day, time } = slot ? { day: slot.day, time: slot.time } : parseSlotKey(key);
      groups.set(key, {
        key,
        day,
        time,
        fee: slot ? Number(slot.fee || 0) : null,
        deliveryDate: slot ? slot.deliveryDate : null,
        label: day ? slotLabel({ day, time, deliveryDate: slot?.deliveryDate }) : '',
        lines: [],
      });
    }
    groups.get(key).lines.push(line);
  });

  return sortSlots([...groups.values()]);
}

/**
 * Each unique slot charged once. Null when any meal sits on a slot we have no
 * price for, so the summary can prompt instead of quoting a short total.
 */
export function cartDeliveryFee(cart = [], availableSlots = [], delivery = null) {
  if (!cart.length) return null;
  const groups = groupCartByDelivery(cart, availableSlots, delivery);
  if (groups.some((g) => g.fee == null)) return null;
  return groups.reduce((sum, g) => sum + g.fee, 0);
}
